import { useMemo, type ReactNode } from 'react';
import { Card, Chip, Text } from '@billda/design-system';
import { WorkerTypeChip } from '../../../shared/components/ui/WorkerTypeChip';
import { FinancialSummaryRow } from './FinancialSummaryRow';
import { formatCurrency } from '../../../shared/utils/currency';
import type { PotDetailsDisplayModel } from '../types/potDetails.types';

interface CondensedPotSummaryProps {
  potDetails: PotDetailsDisplayModel;
  showDeductions?: boolean;
  renderAfterFinancials?: ReactNode;
}

export function CondensedPotSummary({
  potDetails,
  showDeductions = true,
  renderAfterFinancials,
}: CondensedPotSummaryProps) {
  // Calculate fixed rate and ad-hoc totals
  const { fixedRateTotal, adHocTotal, fixedRateGangMembers, adHocWorkers } =
    useMemo(() => {
      const gangMembers = potDetails.fixedRateWorkers.filter(
        w => w.isGangMember
      );
      const adhoc = potDetails.fixedRateWorkers.filter(w => !w.isGangMember);
      return {
        fixedRateGangMembers: gangMembers,
        adHocWorkers: adhoc,
        fixedRateTotal: gangMembers.reduce((sum, w) => sum + w.totalAmount, 0),
        adHocTotal: adhoc.reduce((sum, w) => sum + w.totalAmount, 0),
      };
    }, [potDetails.fixedRateWorkers]);

  // Combine all workers for display
  const allWorkers = useMemo(() => {
    const workers: Array<{
      userId: string;
      userName: string;
      type: 'percentage' | 'fixedRate' | 'adhoc';
      amount: number;
      percentage?: number;
    }> = [];

    // Add percentage split workers
    potDetails.gangMemberSplits.forEach(member => {
      workers.push({
        userId: member.userId,
        userName: member.userName,
        type: 'percentage',
        amount: member.amount,
        percentage: member.percentage,
      });
    });

    // Add fixed rate gang members
    fixedRateGangMembers.forEach(worker => {
      workers.push({
        userId: worker.userId,
        userName: worker.userName,
        type: 'fixedRate',
        amount: worker.totalAmount,
      });
    });

    // Add ad-hoc workers
    adHocWorkers.forEach(worker => {
      workers.push({
        userId: worker.userId,
        userName: worker.userName,
        type: 'adhoc',
        amount: worker.totalAmount,
      });
    });

    return workers;
  }, [potDetails.gangMemberSplits, fixedRateGangMembers, adHocWorkers]);

  return (
    <div className="flex flex-col gap-4">
      {/* Financial Summary Card */}
      {potDetails.financials && (
        <Card className="p-4">
          <Text variant="h6" className="mb-2">
            Financial Summary
          </Text>
          <div className="flex flex-col gap-3">
            <FinancialSummaryRow
              label="Gross Amount"
              value={formatCurrency(potDetails.financials.grossAmount)}
            />
            {fixedRateTotal > 0 && (
              <FinancialSummaryRow
                label="Fixed Rate Workers"
                value={`-${formatCurrency(fixedRateTotal)}`}
                valueColor="error"
              />
            )}
            {adHocTotal > 0 && (
              <FinancialSummaryRow
                label="Ad-hoc Workers"
                value={`-${formatCurrency(adHocTotal)}`}
                valueColor="error"
              />
            )}
            {potDetails.financials.totalDeductions > 0 && (
              <FinancialSummaryRow
                label="Deductions"
                value={`-${formatCurrency(potDetails.financials.totalDeductions)}`}
                valueColor="error"
              />
            )}
            <FinancialSummaryRow
              label="Net Amount"
              value={formatCurrency(potDetails.financials.netAmount)}
              valueColor="primary"
              emphasis
              dividerAbove
            />
          </div>
        </Card>
      )}

      {/* Content to render after financials */}
      {renderAfterFinancials}

      {/* Workers Card - only show if there are workers */}
      {allWorkers.length > 0 && (
        <Card className="p-4">
          <Text variant="h6" className="mb-2">
            Workers ({allWorkers.length})
          </Text>
          <div className="flex flex-col gap-2">
            {allWorkers.map(worker => (
              <div
                key={worker.userId}
                className="flex items-center justify-between py-1"
              >
                <div className="flex items-center gap-2">
                  <Text variant="body2">{worker.userName}</Text>
                  <WorkerTypeChip type={worker.type} />
                  {worker.percentage !== undefined && (
                    <Text variant="caption" color="secondary">
                      ({Math.round(worker.percentage * 100)}%)
                    </Text>
                  )}
                </div>
                <Text variant="body2" className="font-medium">
                  {formatCurrency(worker.amount)}
                </Text>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Deductions Card - only show if there are deductions and showDeductions is true */}
      {showDeductions && potDetails.deductions.length > 0 && (
        <Card className="p-4">
          <Text variant="h6" className="mb-2">
            Deductions (
            {potDetails.deductions.filter(d => d.status !== 'Cancelled').length}
            )
          </Text>
          <div className="flex flex-col gap-2">
            {potDetails.deductions.map(deduction => (
              <div
                key={deduction.id}
                className="flex items-start justify-between"
              >
                <div className="mr-4 flex-1">
                  <Text variant="body2" className="mb-1 font-semibold">
                    {deduction.description}
                  </Text>
                  {deduction.uniqueReference && (
                    <Text variant="body2">{deduction.uniqueReference}</Text>
                  )}
                  {deduction.plotReference && (
                    <Text variant="caption" color="secondary">
                      Plot: {deduction.plotReference}
                    </Text>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {deduction.status === 'Cancelled' && (
                    <Chip
                      label="Cancelled"
                      size="sm"
                      className="font-semibold"
                    />
                  )}
                  <Text
                    variant="body2"
                    className={
                      deduction.status === 'Cancelled'
                        ? 'whitespace-nowrap font-medium text-content-disabled line-through'
                        : 'whitespace-nowrap font-medium'
                    }
                  >
                    {formatCurrency(deduction.amount)}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
